<template>
  <div
    v-if="chips.length"
    class="mb-6 flex gap-2"
    :class="scrollable ? 'flex-nowrap overflow-x-auto pb-1 -mx-1 px-1' : 'flex-wrap'"
  >
    <button
      v-for="chip in chips"
      :key="chip.key"
      type="button"
      class="inline-flex items-center gap-1.5 text-sm font-medium transition-opacity hover:opacity-80"
      :class="[
        scrollable
          ? 'shrink-0 rounded-lg border border-default bg-default px-3 py-2 text-toned shadow-sm'
          : `rounded-full px-3 py-1 ${chipClass(chip.color)}`,
      ]"
      @click="emit('remove', chip.key)"
    >
      <span>{{ chip.label }}</span>
      <UIcon name="i-heroicons-x-mark" class="size-4 shrink-0" />
    </button>
  </div>
</template>

<script setup lang="ts">
import type { Brand, ProductAttribute, ProductFilterState } from '~/types'

const props = defineProps<{
  filters: ProductFilterState
  brands: Brand[]
  attributes: ProductAttribute[]
  scrollable?: boolean
}>()

const emit = defineEmits<{
  remove: [key: string]
}>()

const { stockManagementEnabled } = useSiteSettings()
const productsStore = useProductsStore()

function brandName(brandId: number) {
  return props.brands.find((item) => item.id === brandId)?.name
    ?? productsStore.facets?.brands.find((item) => item.id === brandId)?.name
}

const CHIP_COLORS = ['primary', 'secondary', 'success', 'info', 'warning', 'error'] as const
type ChipColor = typeof CHIP_COLORS[number]

interface FilterChip {
  key: string
  label: string
  color: ChipColor
}

const chips = computed<FilterChip[]>(() => {
  const items: FilterChip[] = []
  let colorIndex = 0
  const nextColor = () => CHIP_COLORS[colorIndex++ % CHIP_COLORS.length]

  for (const brandId of props.filters.brandIds || []) {
    const name = brandName(brandId)
    if (name) {
      items.push({
        key: `brand-${brandId}`,
        label: `Marcă: ${name}`,
        color: nextColor(),
      })
    }
  }

  for (const model of props.filters.models || []) {
    items.push({
      key: `model-${encodeURIComponent(model)}`,
      label: `Model: ${model}`,
      color: nextColor(),
    })
  }

  for (const valueId of props.filters.attributeValueIds || []) {
    for (const attribute of props.attributes) {
      const value = attribute.values?.find((item) => item.id === valueId)
      if (value) {
        items.push({
          key: `attr-${attribute.id}-${valueId}`,
          label: `${attribute.name}: ${value.name}`,
          color: nextColor(),
        })
        break
      }
    }
  }

  if (props.filters.minPrice != null) {
    items.push({
      key: 'minPrice',
      label: `Preț min: ${props.filters.minPrice} RON`,
      color: nextColor(),
    })
  }

  if (props.filters.maxPrice != null) {
    items.push({
      key: 'maxPrice',
      label: `Preț max: ${props.filters.maxPrice} RON`,
      color: nextColor(),
    })
  }

  for (const color of props.filters.colors || []) {
    items.push({
      key: `color-${encodeURIComponent(color)}`,
      label: `Culoare: ${color}`,
      color: nextColor(),
    })
  }

  for (const storage of props.filters.storages || []) {
    items.push({
      key: `storage-${encodeURIComponent(storage)}`,
      label: `Stocare: ${storage}`,
      color: nextColor(),
    })
  }

  if (stockManagementEnabled.value && props.filters.inStock) {
    items.push({ key: 'inStock', label: 'În stoc', color: nextColor() })
  }

  if (props.filters.onSale) {
    items.push({ key: 'onSale', label: 'La reducere', color: nextColor() })
  }

  for (const statusId of props.filters.availabilityStatusIds || []) {
    const name = productsStore.facets?.availability?.find((status) => status.id === statusId)?.name
    if (name) {
      items.push({
        key: `availability-${statusId}`,
        label: name,
        color: nextColor(),
      })
    }
  }

  if (props.filters.refurbishedOnly) {
    items.push({ key: 'refurbishedOnly', label: 'Resigilate', color: nextColor() })
  }

  if (props.filters.search) {
    items.push({
      key: 'search',
      label: `Căutare: ${props.filters.search}`,
      color: nextColor(),
    })
  }

  return items
})

function chipClass(color: ChipColor) {
  const map: Record<ChipColor, string> = {
    primary: 'bg-primary-100 text-primary-800 dark:bg-primary-950/40 dark:text-primary-300',
    secondary: 'bg-violet-100 text-violet-800 dark:bg-violet-950/40 dark:text-violet-300',
    success: 'bg-green-100 text-green-800 dark:bg-green-950/40 dark:text-green-300',
    info: 'bg-sky-100 text-sky-800 dark:bg-sky-950/40 dark:text-sky-300',
    warning: 'bg-amber-100 text-amber-900 dark:bg-amber-950/40 dark:text-amber-300',
    error: 'bg-rose-100 text-rose-800 dark:bg-rose-950/40 dark:text-rose-300',
  }
  return map[color]
}
</script>
